import React, { useState, useEffect, useRef } from 'react';
import LiveStatus from './LiveStatus';
import Chart      from './Chart';
import { useTranslation } from '../i18n/LanguageContext';

// Bot başlatma periyodu — grafiğin görüntüleme periyodundan (Chart.jsx içindeki dropdown) KASITLI
// olarak bağımsız. Bot, hangi periyot burada AÇIKÇA seçiliyse onunla başlar ve ömrü boyunca
// kilitlenir; sadece grafiğe bakmak bu seçimi asla sessizce değiştirmez.
const getStartIntervals = (t) => [
  { label: t('backtest.interval1m'),  value: '1m'  },
  { label: t('backtest.interval5m'),  value: '5m'  },
  { label: t('backtest.interval15m'), value: '15m' },
  { label: t('backtest.interval30m'), value: '30m' },
  { label: '1h',   value: '1H'  },
  { label: '4h',   value: '4H'  },
  { label: '1D',   value: '1D'  },
];

export default function MonitorPanel({ sessions, watchedTrades, analyses, openTrades, events, token, subscription, userType, onUnwatch, onStop, onActiveSymbolChange, settingsSavedAt, tracedSymbols, onUnTrace, onStartBot, onManualTrade, onAdjustTpSl, onCloseTrade }) {
  const { t } = useTranslation();
  const START_INTERVALS = getStartIntervals(t);
  const [activeKey, setActiveKey]               = useState(null);
  const [traceStartLoading, setTraceStartLoading] = useState(false);
  const [traceStartMsg,     setTraceStartMsg]     = useState(null);
  const [manualLoading,     setManualLoading]     = useState(false);
  const [startInterval,     setStartInterval]     = useState('5m');
  // Manuel işlem TP/SL — opsiyonel, boş bırakılırsa hesap varsayılanı (Ayarlar > Risk) kullanılır.
  const [slMode,  setSlMode]  = useState('pct'); // 'pct' | 'price'
  const [slValue, setSlValue] = useState('');
  const [tpMode,  setTpMode]  = useState('pct');
  const [tpValue, setTpValue] = useState('');

  // Aktif sekmenin sembolünü takip et; sekme tipi değişince (trace→bot) aynı sembolde kal
  const activeSymbolRef  = useRef(null);
  // Sekme eklendikçe sembol → sıra numarası — bu sıra hiç değişmez
  const tabOrderRef      = useRef(new Map());
  const nextOrderRef     = useRef(0);
  // Bilinen semboller — null: henüz ilk render olmadı
  const knownSymbolsRef  = useRef(null);

  // Ham sekme listesi (tip önceliksiz)
  const rawTabs = [
    ...sessions.map(s => ({
      key:     `bot_${s.symbol}`,
      symbol:  s.symbol,
      label:   s.symbol,
      meta:    null,
      type:    'bot',
      is_test: s.is_test || false,
    })),
    ...(watchedTrades || [])
      .filter(t => !sessions.some(s => s.symbol === t.symbol))
      .map(t => ({
        key:    `watch_${t.id}`,
        symbol: t.symbol,
        label:  t.symbol,
        meta:   t.side?.toUpperCase(),
        type:   'watch',
        trade:  t,
      })),
    ...(tracedSymbols || [])
      .filter(t => !sessions.some(s => s.symbol === t.symbol) && !(watchedTrades || []).some(w => w.symbol === t.symbol))
      .map(t => ({
        key:      `trace_${t.symbol}`,
        symbol:   t.symbol,
        label:    t.symbol,
        meta:     null,
        type:     'trace',
        market:   t.market || 'futures',
        margin:   t.margin,
        leverage: t.leverage,
        quantity: t.quantity,
      })),
  ];

  // Yeni sembollere sıra numarası ata; kapanan sekmeleri temizle
  const activeSymbols = new Set(rawTabs.map(t => t.symbol));
  rawTabs.forEach(tab => {
    if (!tabOrderRef.current.has(tab.symbol)) {
      tabOrderRef.current.set(tab.symbol, nextOrderRef.current++);
    }
  });
  for (const sym of [...tabOrderRef.current.keys()]) {
    if (!activeSymbols.has(sym)) tabOrderRef.current.delete(sym);
  }

  // Sekmeleri ilk eklenme sırasına göre sırala
  const tabs = [...rawTabs].sort(
    (a, b) => (tabOrderRef.current.get(a.symbol) ?? 999) - (tabOrderRef.current.get(b.symbol) ?? 999)
  );

  useEffect(() => {
    const currentSymbols = new Set(tabs.map(t => t.symbol));

    if (tabs.length === 0) {
      setActiveKey(null);
      knownSymbolsRef.current = currentSymbols;
      return;
    }

    // İlk render: mevcut sekmeleri kaydet, otomatik geçiş yapma
    if (knownSymbolsRef.current === null) {
      knownSymbolsRef.current = currentSymbols;
    } else {
      // Yeni eklenen semboller (Takip Et, yeni bot, vb.)
      const newTabs = tabs.filter(t => !knownSymbolsRef.current.has(t.symbol));
      knownSymbolsRef.current = currentSymbols;

      if (newTabs.length > 0) {
        // En son eklenen sekmeye geç
        const newest = newTabs[newTabs.length - 1];
        setActiveKey(newest.key);
        activeSymbolRef.current = newest.symbol;
        return;
      }
    }

    // Aktif sekme hâlâ var — sembol referansını güncelle
    const currentTab = tabs.find(t => t.key === activeKey);
    if (currentTab) {
      activeSymbolRef.current = currentTab.symbol;
      return;
    }

    // Aktif sekme kayboldu — aynı sembol için yeni sekmeye geç
    const sym = activeSymbolRef.current;
    const sameSymbolTab = sym ? tabs.find(t => t.symbol === sym) : null;
    setActiveKey(sameSymbolTab ? sameSymbolTab.key : tabs[0].key);
  }); // eslint-disable-line

  const active      = tabs.find(t => t.key === activeKey) || tabs[0] || null;
  const isDemoMode  = userType === 'user' && subscription !== 'active';

  useEffect(() => {
    if (active?.symbol) onActiveSymbolChange?.(active.symbol);
  }, [active?.symbol]); // eslint-disable-line

  const symbol       = active?.symbol || null;
  const analysis     = symbol ? (analyses[symbol] || null) : null;
  const session      = symbol ? sessions.find(s => s.symbol === symbol) : null;
  const symbolTrades = symbol ? openTrades.filter(t => t.symbol === symbol) : [];
  const openTrade    = symbolTrades[0] || null;

  const isSpot = active?.market === 'spot';

  const handleManual = async (side, isTest = false) => {
    if (!active) return;
    const loadKey = isTest ? `${side}_test` : side;
    setManualLoading(loadKey);
    setTraceStartMsg(null);
    try {
      const tpsl = {
        is_test: isTest,
        sl_mode: slValue !== '' ? slMode : null, sl_value: slValue !== '' ? slValue : null,
        tp_mode: tpValue !== '' ? tpMode : null, tp_value: tpValue !== '' ? tpValue : null,
      };
      const data = await onManualTrade?.(isSpot
        ? { symbol: active.symbol, market: 'spot', quantity: active.quantity, side, ...tpsl }
        : { symbol: active.symbol, market: 'futures', margin: active.margin, leverage: active.leverage, side, ...tpsl });
      const openedText = t('monitorPanel.openedMsg', { side: `${side.toUpperCase()}${isTest ? ' (Test)' : ''}` });
      // Kontrat büyüklüğü nedeniyle gerçekleşen marjin istenenden belirgin şekilde farklıysa
      // (bkz. trading.js::openTrade marginWarning) kullanıcıyı ayrıca bilgilendir.
      const text = data?.marginWarning ? `${openedText} ⚠ ${data.marginWarning}` : openedText;
      setTraceStartMsg({ text, type: 'ok' });
      setTimeout(() => setTraceStartMsg(null), data?.marginWarning ? 9000 : 4000);
    } catch (err) {
      setTraceStartMsg({ text: err.message, type: 'err' });
      setTimeout(() => setTraceStartMsg(null), 5000);
    } finally {
      setManualLoading(false);
    }
  };

  const handleCloseManual = async () => {
    if (!openTrade) return;
    setManualLoading('close');
    setTraceStartMsg(null);
    try {
      await onCloseTrade?.(openTrade.id);
      setTraceStartMsg({ text: t('monitorPanel.closedMsg'), type: 'ok' });
      setTimeout(() => setTraceStartMsg(null), 4000);
    } catch (err) {
      setTraceStartMsg({ text: err.message, type: 'err' });
      setTimeout(() => setTraceStartMsg(null), 5000);
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <div className="monitor-panel">

      {/* ── Sekme çubuğu ── */}
      {tabs.length > 0 && (
        <div className="monitor-tabs">
          <div className="monitor-tabs-list">
            {tabs.map(tab => (
              <button
                key={tab.key}
                className={`monitor-tab${tab.key === activeKey ? ' active' : ''} monitor-tab-${tab.type}`}
                onClick={() => setActiveKey(tab.key)}
                title={
                  tab.type === 'bot'   ? t('monitorPanel.botRunningTitle') :
                  tab.type === 'watch' ? t('monitorPanel.watchingTitle') : undefined
                }
              >
                {tab.type === 'bot' && !tab.is_test && <span className="monitor-tab-pin">⚙</span>}
                {tab.type === 'bot' && tab.is_test  && <span className="monitor-tab-pin" style={{ color: '#a161d2' }}>⚗</span>}
                {tab.type === 'watch' && <span className="monitor-tab-dot" />}
                {tab.type === 'trace' && <span className="monitor-tab-trace-dot" />}
                <span className="monitor-tab-symbol">{tab.label}</span>
                {tab.meta && <span className="monitor-tab-meta">{tab.meta}</span>}
                {/* bot ve watch sekmeleri sabitlenir — yalnızca trace kapatılabilir */}
                {tab.type === 'trace' && (
                  <span className="monitor-tab-close" onClick={e => { e.stopPropagation(); onUnTrace?.(tab.symbol); }} title={t('monitorPanel.closeTraceTitle')}>×</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bot sekmesi: Durdur butonu */}
      {active?.type === 'bot' && onStop && (
        <div className="chart-action-bar">
          <button className="monitor-stop-btn" onClick={() => onStop(active.symbol)}>
            {t('monitorPanel.stopBot', { symbol: active.symbol })}
          </button>
        </div>
      )}

      {/* Watch sekmesi: açık işlem YOKSA (ör. işlem başka bir yoldan kapanmış ama izleme askıda
          kalmış) izlemeyi bırakma butonu — açık işlem VARKEN gösterilmez, çünkü stopWatch backend'de
          SL/TP izleme döngüsünü durdurur (bkz. botManager.js::stopWatch) ve gerçek bir pozisyonu
          izlemesiz bırakmak istenmez; o durumda doğru yol işlemin kendisini kapatmaktır (tab bunu
          zaten otomatik takip eder). */}
      {active?.type === 'watch' && !openTrade && onUnwatch && (
        <div className="chart-action-bar">
          <button className="monitor-stop-btn" onClick={() => onUnwatch(active.symbol)}>
            {t('monitorPanel.stopWatching')}
          </button>
        </div>
      )}

      {/* Trace sekmesi: Manuel İşlem + Bot Başlat */}
      {active?.type === 'trace' && (
        <div className="chart-action-bar" style={{ flexWrap: 'wrap', rowGap: 8 }}>
          {/* Spot'ta short açılamaz (borçlanma/marjin yok) — bu yüzden spot'ta sadece Long butonları
              gösterilir, Short/Short(Test) hiç render edilmez. */}
          <div className="chart-manual-panel">
            <span className="chart-manual-label">{t('monitorPanel.manualTrade')}</span>
            <button
              style={{ flex: '0 0 auto', padding: '5px 16px', fontSize: 12, fontWeight: 700,
                background: '#16a34a22', border: '1px solid #16a34a', color: '#16a34a',
                borderRadius: 6,
                cursor: (openTrade || isDemoMode) ? 'not-allowed' : 'pointer',
                opacity: (openTrade || isDemoMode) ? .4 : 1 }}
              disabled={manualLoading !== false || traceStartLoading || !!openTrade || isDemoMode}
              title={isDemoMode ? t('monitorPanel.demoModeBlocked') : openTrade ? (openTrade.signal_type === 'manual' ? t('monitorPanel.alreadyOpenManual') : t('monitorPanel.alreadyOpenBot')) : undefined}
              onClick={() => handleManual('long', false)}
            >
              {manualLoading === 'long' ? '…' : (isSpot ? t('monitorPanel.spotStart') : t('monitorPanel.long'))}
            </button>
            {!isSpot && (
            <button
              style={{ flex: '0 0 auto', padding: '5px 16px', fontSize: 12, fontWeight: 700,
                background: '#dc262622', border: '1px solid #dc2626', color: '#dc2626',
                borderRadius: 6,
                cursor: (openTrade || isDemoMode) ? 'not-allowed' : 'pointer',
                opacity: (openTrade || isDemoMode) ? .4 : 1 }}
              disabled={manualLoading !== false || traceStartLoading || !!openTrade || isDemoMode}
              title={isDemoMode ? t('monitorPanel.demoModeBlocked') : openTrade ? (openTrade.signal_type === 'manual' ? t('monitorPanel.alreadyOpenManual') : t('monitorPanel.alreadyOpenBot')) : undefined}
              onClick={() => handleManual('short', false)}
            >
              {manualLoading === 'short' ? '…' : t('monitorPanel.short')}
            </button>
            )}
            {/* Test — gerçek para/OKX emri yok, is_test:true ile simülasyon. Bot'un mor test rengiyle
                tutarlı; isDemoMode'dan BAĞIMSIZ her zaman görünür (bot test butonuyla aynı davranış). */}
            <button
              style={{ flex: '0 0 auto', padding: '5px 16px', fontSize: 12, fontWeight: 700,
                background: 'rgba(161,97,210,0.12)', border: '1px solid #a161d2', color: '#a161d2',
                borderRadius: 6,
                cursor: openTrade ? 'not-allowed' : 'pointer',
                opacity: openTrade ? .4 : 1 }}
              disabled={manualLoading !== false || traceStartLoading || !!openTrade}
              title={openTrade ? (openTrade.signal_type === 'manual' ? t('monitorPanel.alreadyOpenManual') : t('monitorPanel.alreadyOpenBot')) : t('monitorPanel.simulationHint')}
              onClick={() => handleManual('long', true)}
            >
              {manualLoading === 'long_test' ? '…' : (isSpot ? t('monitorPanel.spotStartTest') : t('monitorPanel.longTest'))}
            </button>
            {!isSpot && (
            <button
              style={{ flex: '0 0 auto', padding: '5px 16px', fontSize: 12, fontWeight: 700,
                background: 'rgba(161,97,210,0.12)', border: '1px solid #a161d2', color: '#a161d2',
                borderRadius: 6,
                cursor: openTrade ? 'not-allowed' : 'pointer',
                opacity: openTrade ? .4 : 1 }}
              disabled={manualLoading !== false || traceStartLoading || !!openTrade}
              title={openTrade ? (openTrade.signal_type === 'manual' ? t('monitorPanel.alreadyOpenManual') : t('monitorPanel.alreadyOpenBot')) : t('monitorPanel.simulationHint')}
              onClick={() => handleManual('short', true)}
            >
              {manualLoading === 'short_test' ? '…' : t('monitorPanel.shortTest')}
            </button>
            )}
            {/* Açık işlem BU sekmenin kendi manuel işlemiyse (bot değil) doğrudan buradan kapatılabilir. */}
            {openTrade && openTrade.signal_type === 'manual' && (
              <button
                style={{ flex: '0 0 auto', padding: '5px 16px', fontSize: 12, fontWeight: 700,
                  background: '#dc2626', border: '1px solid #dc2626', color: '#fff',
                  borderRadius: 6, cursor: 'pointer' }}
                disabled={manualLoading !== false || traceStartLoading}
                title={t('monitorPanel.closeManualTitle')}
                onClick={handleCloseManual}
              >
                {manualLoading === 'close' ? '…' : (isSpot ? t('monitorPanel.spotSell') : t('monitorPanel.close'))}
              </button>
            )}
          </div>

          {/* TP/SL (opsiyonel) — boş bırakılırsa Ayarlar > Risk'teki hesap varsayılanı kullanılır. */}
          <div className="chart-manual-panel" style={{ gap: 4 }} title={t('monitorPanel.defaultUsedHint')}>
            <span className="chart-manual-label">{t('monitorPanel.slLabel')}</span>
            <select className="chart-interval-select" style={{ width: 60, padding: '3px 2px' }} value={slMode} onChange={e => setSlMode(e.target.value)} disabled={!!openTrade}>
              <option value="pct">%</option>
              <option value="price">{t('monitorPanel.priceMode')}</option>
              <option value="amount">{t('monitorPanel.amountMode')}</option>
            </select>
            <input
              type="number" step="any" min="0"
              value={slValue} onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) setSlValue(v); }} disabled={!!openTrade}
              style={{ width: 56, fontSize: 12, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 6 }}
            />
            <span className="chart-manual-label" style={{ marginLeft: 4 }}>{t('monitorPanel.tpLabel')}</span>
            <select className="chart-interval-select" style={{ width: 60, padding: '3px 2px' }} value={tpMode} onChange={e => setTpMode(e.target.value)} disabled={!!openTrade}>
              <option value="pct">%</option>
              <option value="price">{t('monitorPanel.priceMode')}</option>
              <option value="amount">{t('monitorPanel.amountMode')}</option>
            </select>
            <input
              type="number" step="any" min="0"
              value={tpValue} onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) setTpValue(v); }} disabled={!!openTrade}
              style={{ width: 56, fontSize: 12, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 6 }}
            />
          </div>

          {onStartBot && (
            <>
              {/* Periyot — AÇIKÇA burada seçilir. Grafik dropdown'ından TAMAMEN bağımsızdır;
                  bot ne görünüyorsa onunla değil, burada seçili olanla başlar ve kilitlenir. */}
              <div className="chart-manual-panel" style={{ marginRight: 4 }}>
                <span className="chart-manual-label">{t('monitorPanel.period')}</span>
                <select
                  className="chart-interval-select"
                  value={startInterval}
                  onChange={e => setStartInterval(e.target.value)}
                  disabled={traceStartLoading || manualLoading !== false || !!openTrade}
                  title={t('monitorPanel.periodLockHint')}
                >
                  {START_INTERVALS.map(iv => <option key={iv.value} value={iv.value}>{iv.label}</option>)}
                </select>
              </div>
              {/* Gerçek bot — demo modda gizli */}
              {!isDemoMode && (
              <button
                className="monitor-trace-start-btn"
                disabled={traceStartLoading || manualLoading !== false || !!openTrade}
                title={openTrade ? (openTrade.signal_type === 'manual' ? t('monitorPanel.alreadyOpenManual') : t('monitorPanel.alreadyOpenBot')) : undefined}
                onClick={async () => {
                  setTraceStartLoading(true);
                  setTraceStartMsg(null);
                  try {
                    await onStartBot(isSpot
                      ? { symbol: active.symbol, market: 'spot', quantity: active.quantity, interval: startInterval }
                      : { symbol: active.symbol, market: 'futures', margin: active.margin, leverage: active.leverage, interval: startInterval });
                  } catch (err) {
                    setTraceStartMsg({ text: err.message, type: 'err' });
                    setTimeout(() => setTraceStartMsg(null), 5000);
                  } finally {
                    setTraceStartLoading(false);
                  }
                }}
              >
                {traceStartLoading ? t('monitorPanel.starting') : t('monitorPanel.startBot', { symbol: active.symbol, interval: startInterval })}
              </button>
              )}
              <button
                className="monitor-trace-start-btn"
                disabled={traceStartLoading || manualLoading !== false || !!openTrade}
                title={t('monitorPanel.simulationHint')}
                style={{ background: 'rgba(161,97,210,0.12)', borderColor: '#a161d2', color: '#a161d2' }}
                onClick={async () => {
                  setTraceStartLoading(true);
                  setTraceStartMsg(null);
                  try {
                    await onStartBot(isSpot
                      ? { symbol: active.symbol, market: 'spot', quantity: active.quantity, interval: startInterval, is_test: true }
                      : { symbol: active.symbol, market: 'futures', margin: active.margin, leverage: active.leverage, interval: startInterval, is_test: true });
                  } catch (err) {
                    setTraceStartMsg({ text: err.message, type: 'err' });
                    setTimeout(() => setTraceStartMsg(null), 5000);
                  } finally {
                    setTraceStartLoading(false);
                  }
                }}
              >
                {traceStartLoading ? t('monitorPanel.starting') : t('monitorPanel.startBotTest', { symbol: active.symbol, interval: startInterval })}
              </button>
            </>
          )}

          {traceStartMsg && (
            <span className={`monitor-trace-msg${traceStartMsg.type === 'err' ? ' err' : ''}`}>
              {traceStartMsg.text}
            </span>
          )}
        </div>
      )}

      {/* ── Grafik ── */}
      {analysis?.symbol && (
        <div className="monitor-chart">
          <Chart
            symbol={analysis.symbol}
            token={token}
            analysisTs={analysis.timestamp}
            currentPrice={analysis.currentPrice}
            openTrade={openTrade}
            settingsSavedAt={settingsSavedAt}
            liveAnalysis={analysis}
            onAdjustTpSl={onAdjustTpSl}
            market={active?.market || 'futures'}
          />
        </div>
      )}

      {/* ── Canlı Durum ── */}
      <LiveStatus analysis={analysis} openTrades={symbolTrades} events={events} rsiDeadzone={session?.rsi_deadzone} market={session?.market || active?.market || 'futures'} />
    </div>
  );
}
